/**
 * Utility-Funktionen für Berechtigungen im Frontend
 */

export interface UserRole {
  role: string;
  displayName: string;
  permissions: string[];
  description: string;
}

/**
 * Mapping von Rollen zu Anzeigenamen und Beschreibungen
 */
export const ROLE_DEFINITIONS: Record<string, UserRole> = {
  admin: {
    role: 'admin',
    displayName: 'Administrator',
    permissions: ['*'],
    description: 'Vollzugriff auf alle Funktionen des Systems'
  },
  schulleitung: {
    role: 'schulleitung',
    displayName: 'Schulleitung',
    permissions: ['view_students', 'edit_students', 'view_reports', 'view_all_stats', 'manage_teachers'],
    description: 'Erweiterte Berechtigungen für die Schulleitung'
  },
  sekretariat: {
    role: 'sekretariat',
    displayName: 'Sekretariat',
    permissions: ['view_students', 'view_reports', 'manage_payments', 'view_financial_reports'],
    description: 'Operative Finanz- und Supportrolle für Zahlungsabwicklung'
  },
  abteilungsleiter: {
    role: 'abteilungsleiter',
    displayName: 'Abteilungsleiter',
    permissions: ['view_students', 'edit_students', 'view_reports', 'view_department_data'],
    description: 'Abteilungsspezifische Berechtigungen'
  },
  klassenlehrer: {
    role: 'klassenlehrer',
    displayName: 'Klassenlehrer',
    permissions: ['view_students', 'edit_students', 'view_reports', 'edit_class_data'],
    description: 'Klassenspezifische Berechtigungen - kann nur Schüler der zugewiesenen Klassen bearbeiten'
  },
  teacher: {
    role: 'teacher',
    displayName: 'Lehrer',
    permissions: ['view_students', 'view_reports'],
    description: 'Grundlegende Lehrerfunktionen - nur Lesezugriff auf Schülerdaten'
  },
  student: {
    role: 'student',
    displayName: 'Schüler',
    permissions: ['view_own_data'],
    description: 'Schülerzugriff - kann nur eigene Daten einsehen'
  }
};

/**
 * Prüft, ob ein Benutzer eine bestimmte Berechtigung hat
 */
export const hasPermission = (userRole: string, permission: string): boolean => {
  const roleDefinition = ROLE_DEFINITIONS[userRole];
  if (!roleDefinition) return false;
  
  // Admin wildcard check
  if (roleDefinition.permissions.includes('*')) {
    return true;
  }
  
  return roleDefinition.permissions.includes(permission);
};

/**
 * Prüft, ob ein Benutzer Schüler bearbeiten darf
 */
export const canEditStudents = (userRole: string): boolean => {
  return hasPermission(userRole, 'edit_students') || hasPermission(userRole, '*');
};

/**
 * Prüft, ob ein Benutzer nur Read-Only Zugriff hat
 */
export const isReadOnlyUser = (userRole: string): boolean => {
  return !canEditStudents(userRole);
};

/**
 * Holt die Anzeige-Informationen für eine Rolle
 */
export const getRoleDisplayInfo = (userRole: string): UserRole | null => {
  return ROLE_DEFINITIONS[userRole] || null;
};

/**
 * Erstellt eine Berechtigungsnachricht für Benutzer
 */
export const getPermissionMessage = (userRole: string): string => {
  const roleInfo = getRoleDisplayInfo(userRole);
  if (!roleInfo) return 'Unbekannte Rolle';
  
  switch (userRole) {
    case 'admin':
      return 'Sie haben Vollzugriff auf alle Funktionen.';
    case 'schulleitung':
      return 'Sie können alle Schüler und Lehrerdaten verwalten.';
    case 'sekretariat':
      return 'Sie können Zahlungen klären, manuell zuordnen und Sonderfälle bearbeiten.';
    case 'abteilungsleiter':
      return 'Sie können alle Schüler und abteilungsspezifische Daten verwalten.';
    case 'klassenlehrer':
      return 'Sie können nur Schüler Ihrer zugewiesenen Klassen bearbeiten.';
    case 'teacher':
      return 'Sie haben nur Lesezugriff auf Schülerdaten.';
    case 'student':
      return 'Sie können nur Ihre eigenen Daten einsehen.';
    default:
      return roleInfo.description;
  }
};

/**
 * Erstellt eine Fehlermeldung für verweigerte Berechtigungen
 */
export const getPermissionDeniedMessage = (userRole: string, action: string, target?: string): string => {
  const roleInfo = getRoleDisplayInfo(userRole);
  const roleName = roleInfo?.displayName || userRole;
  
  switch (action) {
    case 'edit_student':
      if (userRole === 'klassenlehrer') {
        return `Als ${roleName} können Sie nur Schüler Ihrer zugewiesenen Klassen bearbeiten.${target ? ` Schüler aus Klasse ${target} können Sie nicht bearbeiten.` : ''}`;
      }
      return `Als ${roleName} haben Sie nur Lesezugriff auf Schülerdaten. Wenden Sie sich an einen Administrator, Schulleitung oder Abteilungsleiter für Änderungen.`;
    
    case 'delete_student':
      return `Als ${roleName} können Sie keine Schüler löschen. Diese Funktion ist nur für Administratoren, Schulleitung und Abteilungsleiter verfügbar.`;
    
    case 'create_student':
      if (userRole === 'klassenlehrer') {
        return `Als ${roleName} können Sie neue Schüler nur in Ihren zugewiesenen Klassen erstellen.`;
      }
      return `Als ${roleName} können Sie keine neuen Schüler erstellen. Wenden Sie sich an einen Administrator, Schulleitung oder Abteilungsleiter.`;
    
    default:
      return `Als ${roleName} haben Sie keine Berechtigung für diese Aktion.`;
  }
};
